/** A portable PNG carries the same layout into web, PDF and email renderers. */
export const BRAND_LOGO_VERSION = 1;
export const BRAND_LOGO_WIDTH = 1200;
export const BRAND_LOGO_HEIGHT = 400;
export type BrandLogoSettings = {
  version: number;
  scale: number;
  padding: number;
};
export const DEFAULT_BRAND_LOGO_SETTINGS: BrandLogoSettings = {
  version: 1,
  scale: 100,
  padding: 12,
};
export function normalizeBrandLogoSettings(
  value?: Partial<BrandLogoSettings>
): BrandLogoSettings {
  const bounded = (n: unknown, fallback: number, min: number, max: number) =>
    typeof n === "number" && Number.isFinite(n)
      ? Math.min(max, Math.max(min, n))
      : fallback;
  return {
    version: 1,
    scale: bounded(value?.scale, 100, 60, 100),
    padding: bounded(value?.padding, 12, 6, 24),
  };
}
export function brandLogoBounds(
  width: number,
  height: number,
  settings?: Partial<BrandLogoSettings>
) {
  if (!(width > 0 && height > 0)) throw new Error("Invalid logo dimensions");
  const { scale, padding } = normalizeBrandLogoSettings(settings);
  const availableWidth =
    (BRAND_LOGO_WIDTH * (1 - (padding * 2) / 100) * scale) / 100;
  const availableHeight =
    (BRAND_LOGO_HEIGHT * (1 - (padding * 2) / 100) * scale) / 100;
  const ratio = Math.min(availableWidth / width, availableHeight / height);
  return {
    x: (BRAND_LOGO_WIDTH - width * ratio) / 2,
    y: (BRAND_LOGO_HEIGHT - height * ratio) / 2,
    width: width * ratio,
    height: height * ratio,
  };
}
export function loadBrandLogo(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () =>
      image.naturalWidth && image.naturalHeight
        ? resolve(image)
        : reject(new Error("Invalid logo"));
    image.onerror = () => reject(new Error("Logo could not be loaded"));
    image.src = src;
  });
}
export function renderBrandLogo(
  image: HTMLImageElement,
  settings?: Partial<BrandLogoSettings>
): string {
  const canvas = document.createElement("canvas");
  canvas.width = BRAND_LOGO_WIDTH;
  canvas.height = BRAND_LOGO_HEIGHT;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image preview is unavailable");
  const rect = brandLogoBounds(
    image.naturalWidth,
    image.naturalHeight,
    settings
  );
  context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  return canvas.toDataURL("image/png");
}
export const BRAND_LOGO_PROFILES = {
  navigation: { width: 144, height: 44 },
  standard: { width: 188, height: 64 },
  assessment: { width: 120, height: 48 },
  confirmation: { width: 108, height: 43 },
  login: { width: 180, height: 48 },
  avatar: { width: 28, height: 28 },
  pdf: { width: 188, height: 64 },
  invitation: { width: 160, height: 54 },
  email: { width: 240, height: 80 },
} as const;
export type BrandLogoProfile = keyof typeof BRAND_LOGO_PROFILES;
